export function nodeParentId(node) {
  const value = node?.data?.semantic_data?.parent_id;
  return value == null || value === "" ? null : String(value);
}

export function nodeIsCollapsed(node) {
  return Boolean(node?.data?.semantic_data?.collapsed);
}

export function descendantIds(nodes, rootId) {
  const children = new Map();
  nodes.forEach((node) => {
    const parent = nodeParentId(node);
    if (!parent) return;
    if (!children.has(parent)) children.set(parent, []);
    children.get(parent).push(String(node.id));
  });
  const found = new Set();
  const queue = [...(children.get(String(rootId)) || [])];
  while (queue.length) {
    const id = queue.shift();
    if (found.has(id)) continue;
    found.add(id);
    queue.push(...(children.get(id) || []));
  }
  return found;
}

export function hierarchyGraph(nodes, edges) {
  const ids = new Set(nodes.map((node) => String(node.id)));
  const childCounts = new Map();
  nodes.forEach((node) => {
    const parent = nodeParentId(node);
    if (!parent || !ids.has(parent) || parent === String(node.id)) return;
    childCounts.set(parent, (childCounts.get(parent) || 0) + 1);
  });
  const hidden = new Set();
  nodes.forEach((node) => {
    if (!nodeIsCollapsed(node)) return;
    descendantIds(nodes, node.id).forEach((id) => hidden.add(id));
  });
  const visibleNodes = nodes
    .filter((node) => !hidden.has(String(node.id)))
    .map((node) => ({
      ...node,
      data: { ...node.data, child_count: childCounts.get(String(node.id)) || 0 },
    }));
  const visibleIds = new Set(visibleNodes.map((node) => String(node.id)));
  return {
    nodes: visibleNodes,
    edges: edges.filter((edge) => visibleIds.has(String(edge.source)) && visibleIds.has(String(edge.target))),
    hidden,
  };
}

// Collapsible groups are intentionally dormant. Keeping this presentation
// boundary lets a future, map-level feature flag restore them without changing
// stored parent metadata or teaching every viewer about hierarchy internals.
export function mapPresentationGraph(nodes, edges, { collapsibleGroups = false } = {}) {
  return collapsibleGroups
    ? hierarchyGraph(nodes, edges)
    : { nodes, edges, hidden: new Set() };
}

export function availableParents(nodes, nodeId = null) {
  const forbidden = nodeId == null ? new Set() : descendantIds(nodes, nodeId);
  if (nodeId != null) forbidden.add(String(nodeId));
  return nodes
    .filter((node) => !forbidden.has(String(node.id)))
    .map((node) => ({ id: String(node.id), label: node.data?.label || "Untitled node" }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
